import { DateFormat, formatDate } from "@remi/formatting";
import { RemiAvatar, RemiClampedReveal, RemiClampedRevealContent, RemiClampedRevealTrigger } from "@remi/next-ui";

import { usePersonNotes } from "~/hooks/person/usePersonNotes";
import { usePersonProfilePhoto } from "~/hooks/usePerson";
import { cn } from "~/lib/utils";

// Two lines of the note's text-xs body copy, matching the card's former line-clamp-2.
const NOTE_PREVIEW_MAX_HEIGHT_PX = 32;

type PinnedNotePreviewProps = {
  personId: string;
  className?: string;
};

export const PinnedNotePreview = ({ personId, className }: PinnedNotePreviewProps) => {
  const { data: notes = [] } = usePersonNotes(personId);
  const note = notes.filter((n) => n.isPinned).sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())[0];

  const { data: author } = usePersonProfilePhoto(note?.createdById);

  if (!note) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-2 rounded-lg border border-border-yellow bg-background-highlight p-3 text-xs dark:bg-avatar-yellowDark/35",
        className,
      )}
    >
      <div className="flex w-full flex-row items-center gap-1 overflow-hidden">
        {author && (
          <>
            <RemiAvatar size="xs" person={author} url={author.profilePhotoUrl} />
            <span className="font-medium text-text-secondary dark:text-text-faint">{author.contact.name}</span>
          </>
        )}
        <span className="text-text-tertiary dark:text-text-secondary">
          {formatDate(note.updatedAt, { format: DateFormat.MONTH_DAY_LITERAL_AT_TIME })}
        </span>
      </div>
      <RemiClampedReveal previewMaxHeight={NOTE_PREVIEW_MAX_HEIGHT_PX} anchor="top">
        <RemiClampedRevealContent fadeMask={false}>
          <span className="whitespace-pre-wrap text-text-secondary dark:text-text-faint">{note.text}</span>
        </RemiClampedRevealContent>
        <RemiClampedRevealTrigger expandLabel="Show more" collapseLabel="Show less" className="w-fit self-start" />
      </RemiClampedReveal>
    </div>
  );
};
